import React, { useState, useEffect, useRef } from 'react';

const StatsSection = () => {
  // Calculate real-time stats with per-second increments
  const calculateRealTimeStats = () => {
    const now = new Date();
    const baseDate = new Date('2025-10-17'); // Base date - October 17, 2025
    baseDate.setHours(0, 0, 0, 0);
    
    const currentDateOnly = new Date(now);
    currentDateOnly.setHours(0, 0, 0, 0);
    
    // Calculate completed days since base date
    const daysSinceBase = Math.floor((currentDateOnly - baseDate) / (1000 * 60 * 60 * 24));
    
    // Base stats for today (accumulated from previous days)
    let baseRiders = 1000 + (daysSinceBase * 100);
    let baseKm = 10000 + (daysSinceBase * 1000);
    let baseCo2 = 500 + (daysSinceBase * 50);
    
    // Daily increments
    const dailyRidersIncrement = 100;
    const dailyKmIncrement = 1000;
    const dailyCo2Increment = 50;
    
    // Active hours: 6 AM to 12 AM (18 hours = 64,800 seconds)
    const activeStartHour = 6; // 6 AM
    const activeEndHour = 24; // 12 AM (midnight)
    const activeHours = activeEndHour - activeStartHour;
    const totalSecondsInActiveWindow = activeHours * 60 * 60;
    
    // Per-second increments during active window
    const ridersPerSecond = dailyRidersIncrement / totalSecondsInActiveWindow;
    const kmPerSecond = dailyKmIncrement / totalSecondsInActiveWindow;
    const co2PerSecond = dailyCo2Increment / totalSecondsInActiveWindow;
    
    // Check if we're in the active time window (6 AM to 12 AM)
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    
    let todayProgress = 0;
    
    if (currentHour >= activeStartHour && currentHour < activeEndHour) {
      // Calculate seconds elapsed since 6 AM today
      const secondsSince6AM = (currentHour - activeStartHour) * 3600 + currentMinute * 60 + currentSecond;
      todayProgress = Math.min(secondsSince6AM / totalSecondsInActiveWindow, 1);
    } else if (currentHour >= activeEndHour || currentHour < activeStartHour) {
      // After midnight or before 6 AM, show full increment for the day
      todayProgress = 1;
    }
    
    // Calculate current stats with today's progress
    const riders = Math.floor(baseRiders + (dailyRidersIncrement * todayProgress));
    const km = Math.floor(baseKm + (dailyKmIncrement * todayProgress));
    const co2 = Math.floor(baseCo2 + (dailyCo2Increment * todayProgress));
    
    return { riders, km, co2 };
  };

  const [stats, setStats] = useState(calculateRealTimeStats());
  const [counters, setCounters] = useState([0, 0, 0]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  // Update stats every second for real-time effect
  useEffect(() => {
    const interval = setInterval(() => {
      const newStats = calculateRealTimeStats();
      setStats(newStats);
      
      // If already animated, update counters directly
      if (hasAnimated) {
        setCounters([newStats.riders, newStats.km, newStats.co2]);
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [hasAnimated]);

  const statsData = [
    { number: stats.riders, label: 'Happy Riders' },
    { number: stats.km, label: 'Clean Kms Covered' },
    { number: stats.co2, label: 'kgs of CO2 emission reduced' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Initial quick counting animation
            statsData.forEach((stat, index) => {
              const duration = 1500; // 1.5 seconds
              const steps = 60;
              const increment = stat.number / steps;
              let currentStep = 0;

              const timer = setInterval(() => {
                currentStep++;
                setCounters((prev) => {
                  const newCounters = [...prev];
                  newCounters[index] = Math.min(
                    Math.floor(increment * currentStep),
                    stat.number
                  );
                  return newCounters;
                });

                if (currentStep >= steps) {
                  clearInterval(timer);
                }
              }, duration / steps);
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section ref={sectionRef} className="relative -mt-8 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#bceb39] rounded-3xl py-12 px-8 shadow-lg">
          <div className="flex flex-col md:flex-row justify-around items-center gap-12 md:gap-8">
            {statsData.map((stat, index) => (
              <div
                key={index}
                className="text-center"
              >
                <h2 className="text-5xl md:text-6xl font-bold text-black mb-3">
                  {counters[index].toLocaleString()}+
                </h2>
                <p className="text-xl md:text-2xl text-black font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;